'use client';

import { createContext, useContext, useSyncExternalStore, type ReactNode } from 'react';

interface RegionalContextValue {
  currency: string;
  detectedCountry: string | null;
  setCurrency: (code: string) => void;
}

const RegionalContext = createContext<RegionalContextValue>({
  currency: 'USD',
  detectedCountry: null,
  setCurrency: () => {},
});

const currencyListeners = new Set<() => void>();

function subscribeCurrency(listener: () => void) {
  currencyListeners.add(listener);
  return () => currencyListeners.delete(listener);
}

function emitCurrencyChange() {
  for (const listener of currencyListeners) {
    listener();
  }
}

function readCurrencyCookie(fallback: string): string {
  if (typeof document === 'undefined') {
    return fallback;
  }

  const match = document.cookie.match(/(?:^|;\s*)NEXT_CURRENCY=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

function readCountryCookie(fallback: string | null): string | null {
  if (typeof document === 'undefined') {
    return fallback;
  }

  const match = document.cookie.match(/(?:^|;\s*)NEXT_COUNTRY=([^;]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

interface RegionalProviderProps {
  currency: string;
  detectedCountry: string | null;
  children: ReactNode;
}

export function RegionalProvider({ currency, detectedCountry, children }: RegionalProviderProps) {
  const curr = useSyncExternalStore(
    subscribeCurrency,
    () => readCurrencyCookie(currency),
    () => currency,
  );

  const country = useSyncExternalStore(
    subscribeCurrency,
    () => readCountryCookie(detectedCountry),
    () => detectedCountry,
  );

  const setCurrency = (code: string) => {
    document.cookie = `NEXT_CURRENCY=${code}; path=/; max-age=${30 * 86400}; samesite=lax`;
    emitCurrencyChange();
  };

  return (
    <RegionalContext.Provider value={{ currency: curr, detectedCountry: country, setCurrency }}>
      {children}
    </RegionalContext.Provider>
  );
}

export const useRegional = () => useContext(RegionalContext);
